import { Icon } from '@iconify/react';
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from "react-redux";
import { toggleAssignmentFunction, updateStaffFunction } from '../features/staff/staffService';

const EditStaffLayer = () => {
    const { id } = useParams(); 
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // Redux Data
    const staffList = useSelector((state) => state.staffs.staffs) || [];
    const projectList = useSelector((state) => state.projects.projects) || [];

    const staffMember = useMemo(() => 
        staffList.find((s) => String(s.id) === String(id)), 
    [staffList, id]);

    // Form State
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [location, setLocation] = useState(""); 
    const [email, setEmail] = useState(""); 
    const [password, setPassword] = useState("");
    const [role, setRole] = useState("");
    const [selectedProjectIds, setSelectedProjectIds] = useState([]); 
    const [searchTerm, setSearchTerm] = useState("");
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [status, setStatus] = useState("Active"); 
    const [errors, setErrors] = useState({}); 

    useEffect(() => {
        if (staffMember) {
            setName(staffMember.name || "");
            setPhone(staffMember.phone || "");
            setLocation(staffMember.location || "");
            setEmail(staffMember.email || "");
            setPassword(staffMember.plainPassword || ""); 
            setRole(staffMember.role || ""); 
            setStatus(staffMember.status || "Active");
            setSelectedProjectIds(Array.isArray(staffMember.projects) ? staffMember.projects : []);
        }
    }, [staffMember]);

    // Handlers
    const handlePhoneChange = (e) => {
        const value = e.target.value;
        // Accept only digits and limit to 10 characters
        if (/^\d*$/.test(value) && value.length <= 10) {
            setPhone(value);
            // Clear phone error immediately if it becomes valid
            if (value.length === 10 && errors.phone) {
                setErrors(prev => ({ ...prev, phone: null }));
            }
        }
    };

    const validate = () => {
        let newErrors = {};
        if (!name.trim()) newErrors.name = "Full name is required";
        if (!email.trim()) {
            newErrors.email = "Email is required";
        } else if (!/\S+@\S+\.\S+/.test(email)) {
            newErrors.email = "Invalid email format";
        }
        if (!password) {
            newErrors.password = "Password is required";
        } else if (password.length < 6) {
            newErrors.password = "Password must be at least 6 characters";
        }
        if (!role) newErrors.role = "Please select a user role";

        if (!phone.trim()) {
            newErrors.phone = "Phone number is required";
        } else if (phone.length !== 10) {
            newErrors.phone = "Phone number must be 10 digits";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const ErrorMsg = ({ field }) => (
        errors[field] ? (
            <div className="text-danger mt-4 fw-medium" style={{ fontSize: '11px' }}>
                {errors[field]}
            </div>
        ) : null
    );

    const filteredProjects = projectList.filter(proj => {
        const matchesSearch = (proj?.projectName || "").toLowerCase().includes(searchTerm.toLowerCase());
        const safeSelectedIds = Array.isArray(selectedProjectIds) ? selectedProjectIds : [];
        const isNotSelected = !safeSelectedIds.includes(proj.id);
        return matchesSearch && isNotSelected;
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        const currentSelections = Array.isArray(selectedProjectIds) ? selectedProjectIds : [];
        const originalProjects = Array.isArray(staffMember?.projects) ? staffMember.projects : [];

        const payload = {
            id,
            name,
            phone,
            location,
            email,
            plainPassword: password, 
            password: password,
            role,
            projects: currentSelections 
        };

        const addedProjects = currentSelections.filter(pid => !originalProjects.includes(pid));
        const removedProjects = originalProjects.filter(pid => !currentSelections.includes(pid));

        const success = await updateStaffFunction(dispatch, id, payload);
        if (success) navigate("/staff-list");
    };


    // For style
        const transparentStyle = {
        backgroundColor: "transparent",
        border: "none",
        boxShadow: "none",
        backdropFilter: "none"
    };

    return (
        <div className="card h-100 p-0 radius-12" style = {transparentStyle}>
            <div className="card-body p-24">
                <div className="row justify-content-center">
                    <div className="col-xxl-6 col-xl-8 col-lg-10">
                        <div className="card border">
                            <div className="card-body">
                                <h6 className="text-lg text-center text-primary-light mb-16 py-3">Edit Staff Member</h6>
                                <form onSubmit={handleSubmit} noValidate>
        
                                    <div className="mb-20">
                                        <label className="form-label fw-semibold text-primary-light text-sm mb-8">Full Name <span className="text-danger-600">*</span></label>
                                        <input 
                                            type="text" 
                                            className={`form-control radius-8 ${errors.name ? 'border-danger' : ''}`} 
                                            value={name} 
                                            onChange={(e) => setName(e.target.value)} 
                                        />
                                        <ErrorMsg field="name" />
                                    </div>

                                    <div className="mb-20">
                                        <label className="form-label fw-semibold text-primary-light text-sm mb-8">Email Address <span className="text-danger-600">*</span></label>
                                        <input 
                                            type="email" 
                                            className={`form-control radius-8 ${errors.email ? 'border-danger' : ''}`} 
                                            value={email} 
                                            onChange={(e) => setEmail(e.target.value)} 
                                            placeholder="name@company.com" 
                                        />
                                        <ErrorMsg field="email" />
                                    </div>

                                    <div className="mb-20">
                                        <label className="form-label fw-semibold text-primary-light text-sm mb-8">Password <span className="text-danger-600">*</span></label>
                                        <div className="position-relative">
                                            <input 
                                                type={showPassword ? "text" : "password"} 
                                                className={`form-control radius-8 ${errors.password ? 'border-danger' : ''}`} 
                                                value={password} 
                                                onChange={(e) => setPassword(e.target.value)} 
                                                placeholder="Minimum 6 characters" 
                                            />
                                            <button
                                                type="button"
                                                className="position-absolute end-0 top-50 translate-middle-y me-12 border-0 bg-transparent p-0 pt-4 d-flex align-items-center justify-content-center"
                                                onClick={() => setShowPassword(!showPassword)}
                                                style={{ cursor: 'pointer', zIndex: 10 }}
                                            >
                                                <Icon 
                                                    icon={showPassword ? "lucide:eye-off" : "lucide:eye"} 
                                                    className="text-primary-light text-xl" 
                                                />
                                            </button>
                                        </div>
                                        <ErrorMsg field="password" />
                                    </div>

                                    <div className="mb-20">
                                        <label className="form-label fw-semibold text-primary-light text-sm mb-8">User Role <span className="text-danger-600">*</span></label>
                                        <div className="position-relative">
                                            <select 
                                                className={`form-control radius-8 form-select appearance-none ${errors.role ? 'border-danger' : ''}`} 
                                                value={role} 
                                                onChange={(e) => {
                                                    setRole(e.target.value);
                                                    if(errors.role) setErrors(prev => ({...prev, role: null}));
                                                }}
                                            >
                                                <option value="" disabled>Select a role</option> 
                                                <option value="staff">Staff</option>
                                                <option value="designer">Designer</option>
                                            </select>
                                            <div className="position-absolute end-0 top-50 translate-middle-y me-12 pt-8 pointer-events-none">
                                                <Icon 
                                                    icon={role === "Designer" ? "lucide:palette" : "lucide:users"} 
                                                    className={`${errors.role ? 'text-danger' : 'text-primary-light'} text-lg`} 
                                                />
                                            </div>
                                        </div>
                                        <ErrorMsg field="role" />
                                    </div>

                                    <div className="row">
                                        <div className="col-md-6 mb-20">
                                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">Phone Number <span className="text-danger-600">*</span></label>
                                            <input 
                                                type="tel" 
                                                className={`form-control radius-8 ${errors.phone ? 'border-danger' : ''}`} 
                                                value={phone} 
                                                onChange={handlePhoneChange} 
                                                placeholder="10 digit number"
                                            />
                                            <ErrorMsg field="phone" />
                                        </div>
                                        <div className="col-md-6 mb-20">
                                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">Location</label>
                                            <input type="text" className="form-control radius-8" value={location} onChange={(e) => setLocation(e.target.value)} />
                                        </div>
                                    </div>

                                    <div className="d-flex align-items-center justify-content-center gap-3 mt-32">
                                        <button type="submit" className="btn btn-primary px-40 btn-primary">Save Details</button>
                                        <button type='button' onClick={() => navigate(-1)} className="btn border-danger text-danger px-40">Cancel</button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditStaffLayer;